import {
  Button,
  Grid,
  GridItem,
  Modal,
  ModalVariant,
  Split,
  SplitItem,
  Stack,
  TextInput,
  Tooltip,
} from '@patternfly/react-core'
import { GoogleMap, Rectangle, useLoadScript } from '@react-google-maps/api'
import { HelpCircle, Square } from 'lucide-react'
import { useContext, useEffect, useRef, useState } from 'react'
import DatetimeRangePicker from '../../../components/calendar/DatetimeRangePicker'
import PermissionContext from '../../../contexts/PermissionContext'
import useMobile from '../../../hooks/useMobile'
import { AREA_REGEX, INICIAL_VALUES, MAP_STYLE, RECTANGLE_OPTIONS } from '../constants'
import { areaToBounds, boundsToArea } from './areaToBounds'
import { ModalConfirmacao } from './ModalConfirmacao'
import { useRectangleDraw } from './useRectangleDraw'

const { REACT_APP_APIKEY_GOOGLE_MAPS, REACT_APP_MAP_ID_GOOGLE_MAPS } = process.env

const ModalRegistrarDownload = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [tsIn, setTsIn] = useState(INICIAL_VALUES.tsIn)
  const [tsFin, setTsFin] = useState(INICIAL_VALUES.tsFin)
  const isMobile = useMobile()

  const { isLoaded } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: String(REACT_APP_APIKEY_GOOGLE_MAPS),
  })

  const [label, setLabel] = useState('')
  const [area, setArea] = useState(INICIAL_VALUES.area)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [isDrawMode, setDrawMode] = useState(false)
  const [bounds, setBounds] = useState<google.maps.LatLngBoundsLiteral | null>(null)
  const [isConfirmationOpen, setConfirmationOpen] = useState(false)

  const rectangleRef = useRef<google.maps.Rectangle | null>(null)
  const syncingFromMapRef = useRef(false)
  const { me } = useContext(PermissionContext)

  const applyAreaFromRectangle = () => {
    if (isDrawMode) return
    const googleBounds = rectangleRef.current?.getBounds()
    if (!googleBounds) return

    const nextArea = boundsToArea(googleBounds)
    if (nextArea === area) return

    syncingFromMapRef.current = true
    setArea(nextArea)
  }

  useRectangleDraw({
    map,
    enabled: isDrawMode,
    onComplete: (nextBounds, nextArea) => {
      syncingFromMapRef.current = true
      setBounds(nextBounds)
      setArea(nextArea)
      setDrawMode(false)
    },
  })

  useEffect(() => {
    if (syncingFromMapRef.current) {
      syncingFromMapRef.current = false
      return
    }
    if (!AREA_REGEX.test(area)) return

    const timer = setTimeout(() => {
      const { bounds: nextBounds, googleBounds } = areaToBounds(area)
      if (!nextBounds || !googleBounds) return

      setBounds(nextBounds)
      map?.fitBounds(googleBounds, { bottom: 100, top: 100, left: 100, right: 100 })
    }, 400)

    return () => clearTimeout(timer)
  }, [area, map])

  const _onClose = () => {
    onClose()
    setArea(INICIAL_VALUES.area)
    setTsIn(INICIAL_VALUES.tsIn)
    setTsFin(INICIAL_VALUES.tsFin)
    setBounds(null)
    setDrawMode(false)
  }

  return (
    <Modal
      variant={ModalVariant.large}
      title={'Registrar Download'}
      isOpen={isOpen}
      onClose={_onClose}
      actions={[
        <Split hasGutter>
          <SplitItem>
            <Button
              variant='primary'
              onClick={() => setConfirmationOpen(true)}
              isDisabled={!AREA_REGEX.test(area) || !tsIn || !tsFin}
            >
              Registrar Download
            </Button>
          </SplitItem>
          <SplitItem>
            <Button variant='secondary' isDanger onClick={onClose}>
              Cancelar
            </Button>
          </SplitItem>
        </Split>,
      ]}
    >
      <Grid hasGutter>
        <GridItem md={12} lg={5}>
          <Grid hasGutter>
            <GridItem>
              <span>Label</span>
              <TextInput
                isRequired
                type='text'
                id='name-text-input-label'
                value={label ?? ''}
                onChange={(_, value) => setLabel(value)}
              />
            </GridItem>
            <GridItem>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>Área</span>
                <Tooltip
                  content={
                    <Stack>
                      <span>Mais ao norte: -12.5</span>
                      <span>Mais ao sul: -13.0</span>
                      <span>Mais a oeste: -42.25</span>
                      <span>Mais a leste: -41.75</span>
                      <br />
                      <span>Área formatada:</span>
                      <span>-12.5/-42.25/-13.0/-41.75</span>
                    </Stack>
                  }
                >
                  <HelpCircle size={18} />
                </Tooltip>
              </div>
              <TextInput
                isRequired
                type='text'
                id='name-text-input-area'
                placeholder='Norte/Oeste/Sul/Leste'
                value={area ?? ''}
                validated={area && !AREA_REGEX.test(area) ? 'error' : 'default'}
                onChange={(_, value) => setArea(value)}
              />
            </GridItem>
            <GridItem>
              <span>Período</span>
              <DatetimeRangePicker
                value={{ startDate: tsIn, endDate: tsFin }}
                onChange={(newStart, newEnd) => {
                  setTsIn(newStart)
                  setTsFin(newEnd)
                }}
                maxEnabled={new Date(new Date().getFullYear(), new Date().getMonth() - 1)}
                calendarVariant='month'
                variant='label'
              />
            </GridItem>
          </Grid>
        </GridItem>

        {!isMobile && isLoaded && (
          <GridItem md={12} lg={7}>
            <div style={{ position: 'relative' }}>
              <GoogleMap
                onLoad={setMap}
                onMouseUp={applyAreaFromRectangle}
                options={{
                  isFractionalZoomEnabled: true,
                  rotateControl: true,
                  scaleControl: true,
                  mapId: REACT_APP_MAP_ID_GOOGLE_MAPS,
                  mapTypeId: 'satellite',
                  streetViewControl: false,
                }}
                mapContainerStyle={MAP_STYLE}
                center={INICIAL_VALUES.center}
                zoom={9}
              >
                {bounds && !isDrawMode && (
                  <Rectangle
                    onLoad={rect => {
                      rectangleRef.current = rect
                    }}
                    onUnmount={() => {
                      rectangleRef.current = null
                    }}
                    bounds={bounds}
                    options={{
                      ...RECTANGLE_OPTIONS,
                      editable: true,
                      draggable: true,
                    }}
                    onDragEnd={applyAreaFromRectangle}
                  />
                )}
              </GoogleMap>
              <button
                type='button'
                onClick={() => setDrawMode(prev => !prev)}
                title={isDrawMode ? 'Cancelar desenho' : 'Desenhar retângulo'}
                style={{
                  position: 'absolute',
                  top: 60,
                  right: 10,
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  borderRadius: 3,
                  padding: '5px 10px',
                  margin: 0,
                  cursor: 'pointer',
                  boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
                  backgroundColor: isDrawMode ? '#b6b6b6' : '#ffffff',
                }}
              >
                <Square size={18} />
              </button>
            </div>
          </GridItem>
        )}
      </Grid>
      {isConfirmationOpen && (
        <ModalConfirmacao
          isOpen={isConfirmationOpen}
          onClose={() => setConfirmationOpen(false)}
          onSuccess={_onClose}
          data={{
            area,
            ts_in: tsIn.toISOString(),
            ts_fin: tsFin.toISOString(),
            label: label,
            username: me?.current_user?.username,
            status: null,
            triggered_ts: null,
          }}
        />
      )}
    </Modal>
  )
}

export default ModalRegistrarDownload
